import {
  Feedback,
  JumpType,
  TypeArray,
  AnswerType,
  AssetRef,
  Jump,
  TTS
} from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  processActionMeta,
  getArrayTypeReducerGuard,
  getObjectTypeReducerGuard
} from "../reducers"

export type Answer = {
  id: string
  answer: string
  deltas: any
  ocurrence: number
  type: AnswerType
  assets: Array<AssetRef>
  correct: boolean
  feedbacks: Array<Feedback>
  jump: Jump | null
  category: string
  tts: TTS
}

const reducerAction = {
  id: actionCreatorTypeGuard<Answer, string>("id", "Answer"),
  answer: actionCreatorTypeGuard<Answer, string>("answer", "Answer"),
  deltas: actionCreatorTypeGuard<Answer, any>("deltas", "Answer"),
  correct: actionCreatorTypeGuard<Answer, boolean>("correct", "Answer"),
  ocurrence: actionCreatorTypeGuard<Answer, number>("ocurrence", "Answer"),
  type: actionCreatorTypeGuard<Answer, AnswerType>("type", "Answer"),
  assets: actionCreatorTypeGuard<Answer, Array<AssetRef>>("assets", "Answer"),
  tts: actionCreatorTypeGuard<Answer, TTS>("tts", "Answer"),
  feedbacks: actionCreatorTypeGuard<Answer, Array<Feedback>>(
    "feedbacks",
    "Answer"
  ),
  jump: actionCreatorTypeGuard<Answer, Jump | null>("jump", "Answer"),
  __record: actionCreatorTypeGuard<TypeArray, Answer>("Answer", "__record"),
  __create: actionCreatorTypeGuard<TypeArray, Partial<Answer> | null>(
    "create",
    "__create/Answer"
  ),
  category: actionCreatorTypeGuard<Answer, string | null>("category", "answer"),
  __delete: actionCreatorTypeGuard<TypeArray, number>("delete", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof Answer
    | "__record"
    | "__delete"
    | "__create"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const answerAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

export const answerReducer = reducerTypeGuard<Answer>()
  .case(...reducerAction.id)
  .case(...reducerAction.answer)
  .case(...reducerAction.deltas)
  .case(...reducerAction.correct)
  .case(...reducerAction.ocurrence)
  .case(...reducerAction.type)
  .case(...reducerAction.assets)
  .case(...reducerAction.feedbacks)
  .case(...reducerAction.jump)
  .case(...reducerAction.category)
  .case(...reducerAction.tts)
  .default((prevState, action: any) => {
    action = processActionMeta(action)

    switch (action?.meta?.prop) {
      case "feedbacks":
        return getArrayTypeReducerGuard<Answer, Feedback>(
          "answers",
          "feedbacks",
          "Feedback",
          prevState,
          action
        )
      case "assets":
        return getArrayTypeReducerGuard<Answer, AssetRef>(
          "answers",
          "assets",
          "AssetRef",
          prevState,
          action
        )
      case "jump":
        return getObjectTypeReducerGuard<Answer, Jump | null>(
          "answers",
          "jump",
          "Jump",
          prevState,
          action
        )
    }

    return prevState
  })
